import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector, RootState } from '../../../store';
import BaseForm, { BaseFormSectionConfig } from '../../shared/FormComponents/BaseForm';
import PeopleEmailInput from '../../shared/FormComponents/PeopleEmailInput';
import StaticListsService, { StaticRolePermission } from '../../../services/StaticListsService';
import { sendEmployeeInvite } from '../../../store/actions/organization/employeeActions';
import { FormSchema, FormField } from '../../../utils/validation/form-schema';
import { DropdownOption } from '../../shared/TailwindComponents/Dropdown';
import apis from '../../../utils/apis';
import { useFeatureTour } from '../../../hooks/useFeatureTour';
import { FeatureTour, peopleFormTourSteps } from '../../shared/FeatureTour';
import { readErrorMessage } from '../../../utils/errorMessage';
import logger from '../../../utils/logger';

interface RoleChoice {
  role_name: string;
  display_name?: string;
}

interface InviteeItem {
  id: string;
  email: string;
  role: string;
}

interface PeopleFormProps {
  onSuccess?: () => void;
  onCancel?: () => void;
  inviteType?: 'employee' | 'admin';
  seatInfo?: { seatCount: number; seatsUsed: number; seatsAvailable: number };
}

const PeopleForm: React.FC<PeopleFormProps> = ({ onSuccess, onCancel, inviteType = 'employee', seatInfo }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [roles, setRoles] = useState<RoleChoice[]>([]);
  const [rolesLoading, setRolesLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { isRunning: isTourRunning, handleJoyrideCallback: handleTourCallback } = useFeatureTour({
    tourId: 'people-form',
    steps: peopleFormTourSteps,
    readyToShow: !rolesLoading,
    autoStartDelay: 600,
  });

  const orgType = useAppSelector(
    (state: RootState) =>
      (state.user.userInfo as any)?.organization_type ||
      (state.user.userInfo as any)?.OrganizationType ||
      'law_firm',
  );

  useEffect(() => {
    const fetchRoles = async () => {
      try {
        setRolesLoading(true);
        // The org's assignable roles - built-in ones for its org type plus
        // any custom roles - come from `/admin/roles`. The invite used to
        // read the static catalogue hard-wired to `law_firm`, which left
        // non-law-firm orgs with an empty (or partner-only) dropdown.
        const params = inviteType === 'admin' ? undefined : { params: { org_type: orgType } };
        let fetchedRoles: RoleChoice[] = [];
        try {
          const response = await apis.get('/admin/roles', params);
          if (response.data?.success && Array.isArray(response.data.data)) {
            fetchedRoles = response.data.data
              .filter((role: any) => role?.name && role.is_active !== false)
              .map((role: any) => ({
                role_name: String(role.name),
                display_name: role.display_name ? String(role.display_name) : String(role.name),
              }));
          }
        } catch (error: any) {
          logger.warn('Admin roles unavailable, falling back to role catalogue', {
            status: error?.response?.status,
          });
        }

        if (fetchedRoles.length === 0 && inviteType !== 'admin') {
          const catalogue: StaticRolePermission[] = await StaticListsService.getRolePermissions(orgType, true);
          fetchedRoles = catalogue.map((role) => ({
            role_name: role.role_name,
            display_name: role.display_name,
          }));
        }

        const seen = new Set<string>();
        setRoles(
          fetchedRoles.filter((role) => {
            const key = role.role_name.toLowerCase();
            if (seen.has(key)) return false;
            seen.add(key);
            return true;
          }),
        );
      } catch (error: any) {
        logger.error('Error fetching roles', { status: error?.response?.status });
      } finally {
        setRolesLoading(false);
      }
    };
    fetchRoles();
  }, [inviteType, orgType]);

  const roleOptions: DropdownOption[] = roles.map(role => {
    const label = (role.display_name || role.role_name).replace(/_/g, ' ');
    return { value: role.role_name, label: label.charAt(0).toUpperCase() + label.slice(1) };
  });

  // Create validation schema
  const schema = new FormSchema('PeopleForm', [
    new FormField(
      'people',
      'text',
      true,
      'Email Addresses',
      {
        customValidator: (value: any[]) => {
          if (!Array.isArray(value) || value.length === 0) {
            return {
              isValid: false,
              error: t('Please add at least one person'),
            };
          }
          const missingRoles = value.filter(inv => !inv.role);
          if (missingRoles.length > 0) {
            return {
              isValid: false,
              error: t('Please assign a role to all {{count}} invitee(s).', { count: missingRoles.length }),
            };
          }
          return { isValid: true };
        },
      }
    ),
  ]);

  const sections: BaseFormSectionConfig[] = [
    {
      title: t('Recipients'),
      description: t('Add email addresses and assign a role to each person'),
      fields: [
        {
          // The section title above is the heading for this control - a field
          // label here would stack a second one on top of it.
          name: 'people',
          type: 'custom',
          label: '',
          customComponent: PeopleEmailInput,
          customProps: {
            roles: roleOptions,
          },
          required: true,
        },
      ],
    },
  ];

  const handleSubmit = async (data: Record<string, any>) => {
    setIsSubmitting(true);
    try {
      const invites = data.people.map((person: InviteeItem) => ({
        email: person.email,
        role: person.role,
      }));

        await dispatch(sendEmployeeInvite({ invites }) as any);

      onSuccess?.();
    } catch (error: any) {
      const errorMsg = readErrorMessage(error, String(error));
      throw new Error(errorMsg || t('Failed to send invitations. Please try again.'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
    <div className="relative flex h-full min-h-0 flex-col">
    {seatInfo && seatInfo.seatsAvailable <= 3 && (
      <div className={`mx-auto max-w-2xl mb-3 rounded-lg border px-4 py-3 text-sm ${
        seatInfo.seatsAvailable === 0
          ? 'border-danger-300 bg-danger-50 text-danger-700 dark:border-danger-700 dark:bg-danger-900/20 dark:text-danger-400'
          : 'border-warning-300 bg-warning-50 text-warning-700 dark:border-warning-700 dark:bg-warning-900/20 dark:text-warning-400'
      }`}>
        {seatInfo.seatsAvailable === 0
          ? t('No seats available. You must purchase more seats before inviting users.')
          : t('You have {{count}} seat(s) remaining on your current plan.', { count: seatInfo.seatsAvailable })}
      </div>
    )}
    <div data-tour-id="people-form" className="absolute top-0 start-0 end-0 h-[120px] pointer-events-none z-10" />
    <div className="flex-1 min-h-0">
    <BaseForm
      title={inviteType === 'admin' ? t('Invite user') : t('Invite people')}
      description={inviteType === 'admin' ? t('Send invitation emails to add users to your organization.') : t('Send invitation emails to add people to your organization.')}
      sections={sections}
      schema={schema}
      initialValues={{ people: [] }}
      onSubmit={handleSubmit}
      onCancel={onCancel}
      submitLabel={t('Send invitation')}
      cancelLabel={t('Cancel')}
      isSubmitting={isSubmitting}
      maxWidth="max-w-2xl"
      loadingText={t('Sending invitations...')}
    />
    </div>
    </div>
    <FeatureTour
      steps={peopleFormTourSteps}
      run={isTourRunning}
      onCallback={handleTourCallback}
      continuous
      showProgress
      showSkipButton
      disableOverlay
    />
    </>
  );
};

export default PeopleForm;
